import React from 'react';
import { CheckCircle, TrendingUp, Car, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    minInvestment: 5000,
    roi: 12,
    vehicles: 2,
    color: 'from-slate-700 to-slate-800',
    badge: 'bg-slate-600',
    highlight: false,
    payoutSchedule: 'Monthly',
    payoutFrequency: 12,
    duration: '24 months',
    features: [
      '2 fleet vehicles allocated',
      '12% annual ROI',
      'Monthly payout schedule',
      'Basic portfolio dashboard',
      'Email support',
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    minInvestment: 15000,
    roi: 15,
    vehicles: 6,
    color: 'from-[#1a2744] to-[#0f1a35]',
    badge: 'bg-[#c9a84c]',
    highlight: true,
    payoutSchedule: 'Monthly',
    payoutFrequency: 12,
    duration: '24 months',
    features: [
      '6 fleet vehicles allocated',
      '15% annual ROI',
      'Monthly payout schedule',
      'Advanced portfolio analytics',
      'Priority support',
      'Quarterly performance reports',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    minInvestment: 50000,
    roi: 18,
    vehicles: 15,
    color: 'from-[#8B6914] to-[#6B4F0E]',
    badge: 'bg-amber-500',
    highlight: false,
    payoutSchedule: 'Monthly',
    payoutFrequency: 12,
    duration: '24 months',
    features: [
      '15 fleet vehicles allocated',
      '18% annual ROI',
      'Monthly payout schedule',
      'Dedicated account manager',
      'Custom investment terms',
      'Real-time fleet tracking',
      'Tax optimization advisory',
    ],
  },
];

const InvestmentTiers = ({ onSelectTier }) => {
  const navigate = useNavigate();

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 })?.format(amount);

  const calcAnnualReturn = (amount, roi) => (amount * roi) / 100;

  return (
    <section id="tiers" className="mb-16">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-[#1a2744] mb-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Investment Tiers
        </h2>
        <p className="text-slate-500 text-sm">Choose the plan that matches your investment goals</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TIERS?.map((tier) => (
          <div
            key={tier?.id}
            className={`relative rounded-2xl overflow-hidden shadow-lg ${
              tier?.highlight ? 'ring-2 ring-[#c9a84c] scale-[1.02]' : ''
            } transition-transform hover:scale-[1.03]`}
          >
            {tier?.highlight && (
              <div className="absolute top-0 left-0 right-0 bg-[#c9a84c] text-[#1a2744] text-xs font-bold text-center py-1.5 tracking-widest uppercase z-10">
                Most Popular
              </div>
            )}

            <div className={`bg-gradient-to-br ${tier?.color} p-6 ${tier?.highlight ? 'pt-9' : ''}`}>
              <div className="flex items-center justify-between mb-4">
                <span className={`${tier?.badge} text-white text-xs font-semibold px-3 py-1 rounded-full`}>
                  {tier?.name}
                </span>
                <div className="flex items-center gap-1 text-white/80 text-xs">
                  <Car size={14} />
                  <span>{tier?.vehicles} vehicles</span>
                </div>
              </div>

              <div className="mb-1">
                <span className="text-white/60 text-xs">Minimum Investment</span>
                <div className="text-white text-3xl font-bold mt-0.5" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {formatCurrency(tier?.minInvestment)}
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4">
                <TrendingUp size={16} className="text-[#c9a84c]" />
                <span className="text-[#c9a84c] font-bold text-lg">{tier?.roi}% ROI</span>
                <span className="text-white/50 text-xs">per annum</span>
              </div>
            </div>

            <div className="bg-white p-6">
              <div className="grid grid-cols-2 gap-3 mb-5 pb-5 border-b border-slate-100">
                <div>
                  <div className="text-xs text-slate-400 mb-0.5">Annual Return</div>
                  <div className="text-[#1a2744] font-bold text-sm">
                    {formatCurrency(calcAnnualReturn(tier?.minInvestment, tier?.roi))}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-0.5">Payout</div>
                  <div className="text-[#1a2744] font-bold text-sm flex items-center gap-1">
                    <Calendar size={12} />
                    {tier?.payoutSchedule}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-0.5">Duration</div>
                  <div className="text-[#1a2744] font-bold text-sm">{tier?.duration}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-0.5">Monthly Payout</div>
                  <div className="text-[#1a2744] font-bold text-sm">
                    {formatCurrency(calcAnnualReturn(tier?.minInvestment, tier?.roi) / 12)}
                  </div>
                </div>
              </div>

              <ul className="space-y-2 mb-6">
                {tier?.features?.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle size={14} className="text-[#c9a84c] mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => onSelectTier?.(tier)}
                className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
                  tier?.highlight
                    ? 'bg-[#c9a84c] text-[#1a2744] hover:bg-[#b8973b]'
                    : 'bg-[#1a2744] text-white hover:bg-[#0f1a35]'
                }`}
              >
                Start Investing →
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default InvestmentTiers;
